import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useAccounts } from "@/contexts/AccountContext";
import { NAV, ADMIN } from "@/constants/testIds";
import { PartnerBanner } from "@/components/PartnerBanner";

export default function Layout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const { accounts, selectedId, select } = useAccounts();
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-[#F7F7F4]">
      <header
        data-testid={NAV.bar}
        className="sticky top-0 z-50 bg-white border-b border-[#E5E5DE]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <button
            data-testid={NAV.logo}
            onClick={() => nav("/dashboard")}
            className="flex items-center gap-3 shrink-0"
          >
            <div className="w-7 h-7 bg-[#0F3524] flex items-center justify-center">
              <span className="font-mono-data text-[#F7F7F4] text-[10px]">KW</span>
            </div>
            <div className="font-display text-base tracking-tight">Kilowatt Ledger</div>
          </button>

          <nav className="hidden md:flex items-center gap-8">
            {!isAdmin && <NavItem to="/dashboard" testid={NAV.linkDashboard}>Dashboard</NavItem>}
            {!isAdmin && <NavItem to="/upload" testid={NAV.linkUpload}>Upload bill</NavItem>}
            {!isAdmin && <NavItem to="/settings" testid={NAV.linkSettings}>Settings</NavItem>}
            {isAdmin && <NavItem to="/admin" testid={ADMIN.navLink}>Admin</NavItem>}
          </nav>

          <div className="flex items-center gap-3 sm:gap-4">
            {accounts.length > 1 && !isAdmin && (
              <select
                data-testid={NAV.accountPicker}
                value={selectedId || ""}
                onChange={(e) => select(e.target.value)}
                className="hidden sm:block bg-white border border-[#E5E5DE] font-mono-data text-xs px-3 py-2 focus:outline-none focus:border-[#0F3524]"
              >
                {accounts.map((a) => (
                  <option
                    key={a.account_id}
                    data-testid={NAV.accountOption(a.account_id)}
                    value={a.account_id}
                  >
                    {a.name}{a.account_number ? ` · …${a.account_number.slice(-4)}` : ""}
                  </option>
                ))}
              </select>
            )}
            {user?.picture ? (
              <img
                src={user.picture}
                alt={user.name}
                className="w-8 h-8 rounded-full border border-[#E5E5DE]"
                data-testid={NAV.userMenu}
              />
            ) : (
              <div className="w-8 h-8 bg-[#F0F0EA] flex items-center justify-center font-mono-data text-xs">
                {user?.name?.[0] || "?"}
              </div>
            )}
            <button
              data-testid={NAV.logoutBtn}
              onClick={logout}
              className="text-sm font-medium text-[#1A1B1A] hover:text-[#B24F35] transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main>{children}</main>

      <footer className="mt-24 bg-[#F7F7F4] text-[#1A1B1A] border-t border-[#E5E5DE] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center gap-6 text-center">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-[#B24F35] mb-2">Contact</div>
            <a
              href="mailto:alfred.initiatives@gmail.com"
              data-testid="alfred-email-link"
              className="text-sm sm:text-base font-mono-data text-[#1A1B1A] hover:text-[#B24F35] transition-colors"
            >
              alfred.initiatives@gmail.com
            </a>
          </div>
          <PartnerBanner />
          <div className="w-full flex flex-col sm:flex-row justify-between items-center gap-2 pt-4 mt-2 border-t border-[#E5E5DE] text-[#6E716C]">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em]">Kilowatt Ledger · Editorial ·  Beta</div>
            <div className="text-xs font-mono-data">CO₂ factor 0.585 kg/kWh · Peninsular Malaysia</div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function NavItem({ to, testid, children }) {
  return (
    <NavLink
      to={to}
      data-testid={testid}
      className={({ isActive }) =>
        `text-sm font-medium tracking-tight transition-colors ${
          isActive ? "text-[#0F3524] link-underline" : "text-[#6E716C] hover:text-[#0F3524]"
        }`
      }
    >
      {children}
    </NavLink>
  );
}
